<template>
  <div class="tool-page">
    <!-- 面包屑导航 -->
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('mortgage.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('mortgage.title') }}</h1>
      <p>{{ $t('mortgage.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="form-section">
        <div class="form-row">
          <label>{{ $t('mortgage.loanAmount') }}</label>
          <div class="input-group">
            <input type="number" v-model.number="loanAmount" placeholder="">
            <span class="unit">{{ $t('mortgage.unit.tenThousand') }}</span>
          </div>
        </div>

        <div class="form-row">
          <label>{{ $t('mortgage.loanYears') }}</label>
          <div class="input-group">
            <input type="number" v-model.number="loanYears" placeholder="" min="1" max="30">
            <span class="unit">{{ $t('mortgage.unit.year') }}</span>
          </div>
          <div class="quick-options">
            <button v-for="year in [10, 15, 20, 25, 30]" :key="year" 
              :class="['quick-btn', { active: loanYears === year }]"
              @click="loanYears = year">{{ year }}{{ $t('mortgage.unit.year') }}</button>
          </div>
        </div>

        <div class="form-row">
          <label>{{ $t('mortgage.interestRate') }}</label>
          <div class="input-group">
            <input type="number" v-model.number="interestRate" step="0.01" placeholder="">
            <span class="unit">{{ $t('mortgage.unit.percent') }}</span>
          </div>
          <div class="quick-options">
            <button :class="['quick-btn', { active: interestRate === 3.1 }]" @click="interestRate = 3.1">{{ $t('mortgage.housingFund') }} 3.1%</button>
            <button :class="['quick-btn', { active: interestRate === 3.45 }]" @click="interestRate = 3.45">{{ $t('mortgage.commercial') }} 3.45%</button>
            <button :class="['quick-btn', { active: interestRate === 4.2 }]" @click="interestRate = 4.2">{{ $t('mortgage.commercial') }} 4.2%</button>
          </div>
        </div>

        <div class="form-row">
          <label>{{ $t('mortgage.paymentMethod') }}</label>
          <div class="radio-group">
            <label class="radio-label">
              <input type="radio" v-model="paymentMethod" value="equal">
              <span>{{ $t('mortgage.equalPayment') }}</span>
            </label>
            <label class="radio-label">
              <input type="radio" v-model="paymentMethod" value="principal">
              <span>{{ $t('mortgage.equalPrincipal') }}</span>
            </label>
          </div>
        </div>

        <button class="btn primary calculate-btn" @click="calculate">{{ $t('mortgage.calculate') }}</button>
      </div>

      <div class="result-section" v-if="result">
        <h3>{{ $t('mortgage.results') }}</h3>
        
        <div class="result-cards">
          <div class="result-card highlight">
            <div class="card-label">{{ paymentMethod === 'equal' ? $t('mortgage.monthlyPayment') : $t('mortgage.firstMonthPayment') }}</div>
            <div class="card-value">¥ {{ formatMoney(result.monthlyPayment) }}</div>
          </div>
          <div class="result-card">
            <div class="card-label">{{ $t('mortgage.totalLoan') }}</div>
            <div class="card-value">¥ {{ formatMoney(result.totalLoan) }}</div>
          </div>
          <div class="result-card">
            <div class="card-label">{{ $t('mortgage.totalInterest') }}</div>
            <div class="card-value">¥ {{ formatMoney(result.totalInterest) }}</div>
          </div>
          <div class="result-card">
            <div class="card-label">{{ $t('mortgage.totalPayment') }}</div>
            <div class="card-value">¥ {{ formatMoney(result.totalPayment) }}</div>
          </div>
        </div>

        <div class="detail-section">
          <h4>{{ $t('mortgage.details') }}</h4>
          <div class="table-wrapper">
            <table class="detail-table">
              <thead>
                <tr>
                  <th>{{ $t('mortgage.period') }}</th>
                  <th>{{ $t('mortgage.payment') }}</th>
                  <th>{{ $t('mortgage.principal') }}</th>
                  <th>{{ $t('mortgage.interest') }}</th>
                  <th>{{ $t('mortgage.remaining') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in result.details.slice(0, 12)" :key="item.period">
                  <td>{{ item.period }}</td>
                  <td>{{ formatMoney(item.payment) }}</td>
                  <td>{{ formatMoney(item.principal) }}</td>
                  <td>{{ formatMoney(item.interest) }}</td>
                  <td>{{ formatMoney(item.remaining) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 工具说明 - removed as too long, keeping layout -->

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('mortgage.whatIsMortgage') }}</h2>
      <p>{{ $t('mortgage.whatIsMortgageDesc') }}</p>

      <h2>{{ $t('mortgage.repaymentMethods') }}</h2>
      <h3>{{ $t('mortgage.equalPaymentExplain') }}</h3>
      <p>{{ $t('mortgage.equalPaymentExplainDesc') }}</p>
      <h3>{{ $t('mortgage.equalPrincipalExplain') }}</h3>
      <p>{{ $t('mortgage.equalPrincipalExplainDesc') }}</p>

      <h2>{{ $t('mortgage.comparisonTitle') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('mortgage.comparisonItems')" :key="'cmp'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('mortgage.interestRateTypes') }}</h2>
      <p>{{ $t('mortgage.interestRateTypesDesc') }}</p>

      <h2>{{ $t('mortgage.prepaymentStrategies') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('mortgage.prepaymentStrategiesItems')" :key="'pre'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('mortgage.buyingTips') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('mortgage.buyingTipsItems')" :key="'tip'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('mortgage.lprExplain') }}</h2>
      <p>{{ $t('mortgage.lprExplainDesc') }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MortgageCalculator',
  data() {
    return {
      loanAmount: 100,
      loanYears: 30,
      interestRate: 3.45,
      paymentMethod: 'equal',
      result: null
    }
  },
  methods: {
    calculate() {
      const principal = this.loanAmount * 10000 // 转换为元
      const months = this.loanYears * 12
      const monthlyRate = this.interestRate / 100 / 12

      if (this.paymentMethod === 'equal') {
        this.calculateEqualPayment(principal, months, monthlyRate)
      } else {
        this.calculateEqualPrincipal(principal, months, monthlyRate)
      }
    },

    // 等额本息
    calculateEqualPayment(principal, months, monthlyRate) {
      const monthlyPayment = principal * monthlyRate * Math.pow(1 + monthlyRate, months) / 
        (Math.pow(1 + monthlyRate, months) - 1)
      
      const totalPayment = monthlyPayment * months
      const totalInterest = totalPayment - principal

      const details = []
      let remaining = principal
      
      for (let i = 1; i <= months; i++) {
        const interest = remaining * monthlyRate
        const principalPart = monthlyPayment - interest
        remaining -= principalPart
        
        details.push({
          period: i,
          payment: monthlyPayment,
          principal: principalPart,
          interest: interest,
          remaining: Math.max(0, remaining)
        })
      }

      this.result = {
        monthlyPayment,
        totalLoan: principal,
        totalInterest,
        totalPayment,
        details
      }
    },

    // 等额本金
    calculateEqualPrincipal(principal, months, monthlyRate) {
      const monthlyPrincipal = principal / months
      const details = []
      let remaining = principal
      let totalInterest = 0

      for (let i = 1; i <= months; i++) {
        const interest = remaining * monthlyRate
        const payment = monthlyPrincipal + interest
        remaining -= monthlyPrincipal
        totalInterest += interest
        
        details.push({
          period: i,
          payment: payment,
          principal: monthlyPrincipal,
          interest: interest,
          remaining: Math.max(0, remaining)
        })
      }

      this.result = {
        monthlyPayment: details[0].payment, // 首月还款
        totalLoan: principal,
        totalInterest,
        totalPayment: principal + totalInterest,
        details
      }
    },

    formatMoney(value) {
      return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 900px;
  margin: 0 auto;
}

/* 面包屑导航 */
.breadcrumb {
  margin-bottom: 24px;
  font-size: 14px;
  color: var(--text-secondary);
}

.breadcrumb a {
  color: var(--accent-color);
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.breadcrumb .separator {
  margin: 0 8px;
  color: var(--text-muted);
}

.breadcrumb .current {
  color: var(--text-secondary);
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  font-size: 16px;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
}

.form-section {
  margin-bottom: 32px;
}

.form-row {
  margin-bottom: 20px;
}

.form-row > label {
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.input-group {
  display: flex;
  align-items: center;
}

.input-group input {
  flex: 1;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--text-primary);
  font-size: 16px;
}

.input-group input:focus {
  outline: none;
  border-color: var(--accent-color);
}

.input-group .unit {
  margin-left: 12px;
  color: var(--text-secondary);
  font-size: 14px;
}

.quick-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.quick-btn {
  padding: 6px 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-btn:hover {
  background: var(--bg-hover);
}

.quick-btn.active {
  background: var(--accent-light);
  border-color: var(--accent-color);
  color: var(--accent-color);
}

.radio-group {
  display: flex;
  gap: 24px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: var(--text-primary);
}

.radio-label input {
  width: 18px;
  height: 18px;
  accent-color: var(--accent-color);
}

.calculate-btn {
  width: 100%;
  padding: 14px;
  font-size: 16px;
  margin-top: 12px;
}

.btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn.primary {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

.btn.primary:hover {
  background: var(--accent-hover);
}

.result-section h3 {
  font-size: 20px;
  margin-bottom: 20px;
  color: var(--text-primary);
}

.result-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 32px;
}

@media (max-width: 768px) {
  .result-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

.result-card {
  background: var(--bg-tertiary);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
}

.result-card.highlight {
  background: var(--accent-light);
  border: 1px solid var(--accent-color);
}

.card-label {
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.card-value {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
}

.result-card.highlight .card-value {
  color: var(--accent-color);
}

.detail-section h4 {
  font-size: 16px;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.table-wrapper {
  overflow-x: auto;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.detail-table th,
.detail-table td {
  padding: 12px;
  text-align: right;
  border-bottom: 1px solid var(--border-color);
}

.detail-table th {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-weight: 500;
}

.detail-table td {
  color: var(--text-primary);
}

.detail-table th:first-child,
.detail-table td:first-child {
  text-align: center;
}

/* 工具说明 */
.tool-description {
  margin-top: 32px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  box-shadow: var(--shadow-sm);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 24px 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--accent-light);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 16px;
}

.tool-description ul {
  padding-left: 24px;
  margin-bottom: 16px;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 8px;
}

.tool-description strong {
  color: var(--text-primary);
}
</style>
